import { type ConfigValue, isRecord } from "./value";

// A path through the document and the scalar at the end of it. The segments are kept apart from the separator that
// joins them, since `.env` spells a nesting `__` and `.properties` spells it `.`, and neither is this module's business.
export interface FlatEntry {
  path: string[];
  value: ConfigValue;
}

// A container with nothing in it has no scalar to write down, so it is reported rather than quietly dropped.
export function flatten(value: ConfigValue): { entries: FlatEntry[]; lost: string[] } {
  const entries: FlatEntry[] = [];
  const lost: string[] = [];

  const walk = (node: ConfigValue, path: string[]) => {
    if (Array.isArray(node)) {
      if (node.length === 0) return lost.push(displayPath(path));
      return node.forEach((item, index) => walk(item, [...path, String(index)]));
    }
    if (isRecord(node)) {
      const keys = Object.keys(node);
      if (keys.length === 0) return lost.push(displayPath(path));
      return keys.forEach((key) => walk(node[key], [...path, key]));
    }
    entries.push({ path, value: node });
  };

  walk(value, []);
  return { entries, lost };
}

// The other direction, and the one place the flattening is guessed rather than known: a segment that is a run of digits
// counting up from zero is read back as the array index it was written from, which a key genuinely called "0" is not.
export function nest(entries: FlatEntry[]): ConfigValue {
  const root: { [key: string]: ConfigValue } = {};

  for (const { path, value } of entries) {
    if (path.length === 0) continue;
    let node = root;
    for (const key of path.slice(0, -1)) {
      // A name used as both a leaf and a branch keeps the branch: the deeper reading is the one with somewhere to go.
      if (!isRecord(node[key])) node[key] = {};
      node = node[key] as { [key: string]: ConfigValue };
    }
    node[path[path.length - 1]] = value;
  }

  return arraysWhereIndexed(root);
}

// How a path is named to somebody reading the page, which is the same however the format being written spells it.
export function displayPath(path: string[]): string {
  return path.length === 0 ? "(root)" : path.join(".");
}

function arraysWhereIndexed(node: ConfigValue): ConfigValue {
  if (!isRecord(node)) return node;

  const walked: { [key: string]: ConfigValue } = {};
  for (const key of Object.keys(node)) walked[key] = arraysWhereIndexed(node[key]);

  // Exactly 0 to n-1 and nothing besides, which is the only shape an index can have flattened to. A key that looks
  // like an integer is listed first and in numeric order whatever order it arrived in, so this reads them sorted.
  const keys = Object.keys(walked);
  const indexed = keys.length > 0 && keys.every((key, index) => key === String(index));
  return indexed ? keys.map((key) => walked[key]) : walked;
}
